import React, { Suspense, useEffect, useMemo, useState } from 'react';
import AutomotiveBreadcrumbs from '../automotivePage/AutomotiveBreadcrumbs';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApplicationsContent } from '../../graphql/pimcore/modules/siteContent/types/ApplicationsContent';
import {
    ComputingComponentName as ComponentName,
    useComputingComponents,
} from './ComputingDisplayComponents';
import { buildSlugMaps } from '../../utils/slug';

const toCamelCase = (str: string) => {
    return str.replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
};

interface ComputingDisplayProps {
    contentTree: ApplicationsContent;
}

const ComputingDisplay: React.FC<ComputingDisplayProps> = ({ contentTree }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const { components } = useComputingComponents(contentTree);

    const [componentStack, setComponentStack] = useState<ComponentName[]>([
        'computingEnterpriseSystems',
    ]);

    /* ---------- slug helpers ---------- */
    const { nameToSlug, slugToName } = useMemo(() => buildSlugMaps(components), [components]);

    useEffect(() => {
        const pathSegments = location.pathname.toLowerCase().split('/').filter(Boolean);

        const computingIndex = pathSegments.indexOf('computing-enterprisesystems');

        let relevantSegments: string[] = [];
        if (computingIndex !== -1) {
            relevantSegments = pathSegments.slice(computingIndex + 1);
        }

        if (relevantSegments.length === 0) {
            setComponentStack(['computingEnterpriseSystems']);
        } else {
            const componentNames: ComponentName[] = relevantSegments.map((segment) => {
                const name = slugToName[segment];
                return name ?? 'computingEnterpriseSystems';
            });
            setComponentStack(['computingEnterpriseSystems', ...componentNames]);
        }
    }, [location.pathname, slugToName]);

    const handleComponentSwitch = (rawName: ComponentName | string) => {
        const internalName = (
            rawName in nameToSlug ? rawName : slugToName[rawName]
        ) as ComponentName;
        const newComponentStack = [...componentStack, internalName];
        setComponentStack(newComponentStack);
        const slugPath = newComponentStack
            .slice(1)
            .map((n) => nameToSlug[n])
            .join('/');

        navigate(`/applications/computing-enterprisesystems/${slugPath}`, { replace: false });
    };
    const handleBack = () => {
        if (componentStack.length > 1) {
            const newComponentStack = componentStack.slice(0, -1);
            setComponentStack(newComponentStack);
            const slugPath = newComponentStack
                .slice(1)
                .map((n) => nameToSlug[n])
                .join('/');
            navigate(`/applications/computing-enterprisesystems/${slugPath}`, { replace: false });
        } else {
            setComponentStack(['computingEnterpriseSystems']);
            navigate('/applications/computing-enterprisesystems', { replace: false });
        }
    };

    const fadeVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
    };

    const fadeTransition = {
        duration: 0.8,
        ease: 'easeInOut',
    };

    const handleBreadcrumbClick = (index: number) => {
        const newComponentStack = componentStack.slice(0, index + 1);
        setComponentStack(newComponentStack);
        const slugPath = newComponentStack
            .slice(1)
            .map((n) => nameToSlug[n])
            .join('/');
        navigate(`/applications/computing-enterprisesystems/${slugPath}`, { replace: false });
    };

    const currentComponentName = componentStack[componentStack.length - 1];

    const currentComponentInfo = currentComponentName
        ? components[currentComponentName]
        : undefined;

    const breadcrumbItems = componentStack.map((componentName, index) => ({
        label: (components[componentName]?.label || componentName).toString(),
    }));

    const commonProps = {
        onBack: handleBack,
        onAnnotationClick: handleComponentSwitch,
    };

    return (
        <>
            <AutomotiveBreadcrumbs
                items={breadcrumbItems}
                onBreadcrumbClick={handleBreadcrumbClick}
            />
            <Typography
                sx={{ marginBottom: '2rem', fontFamily: 'Poppins', fontSize: '16px' }}
            ></Typography>
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentComponentName}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={fadeVariants}
                    transition={fadeTransition}
                >
                    <Suspense fallback={<div>Loading...</div>}>
                        {currentComponentInfo?.imageComponent ? (
                            <currentComponentInfo.imageComponent onBack={handleBack} />
                        ) : currentComponentInfo?.component ? (
                            <currentComponentInfo.component {...commonProps} />
                        ) : null}
                    </Suspense>
                </motion.div>
            </AnimatePresence>
        </>
    );
};

export default ComputingDisplay;
